import React, { useCallback, useRef, useState } from 'react';
import type { MouseEvent } from 'react';

import { formatVideoTime } from '../../utils/video';
import { FullScreen, Minimize, Player, Velocity, Volume } from '../Controls';
import * as S from '../../styles';

interface DraggableLayoutProps {
	visible?: boolean;
	volume: number;
	playing: boolean;
	minimized: boolean;
	playbackRate: number;
	progress: number;
	duration: number;
	fullscreen?: boolean;
	showVolume?: boolean;
	showFullscreen?: boolean;
	onTogglePlay: () => void;
	onToggleSound: () => void;
	onToggleMinimized: () => void;
	onToggleFullscreen?: () => void;
	onVolumeChange: (volume: number) => void;
	onPlaybackRateChange: (rate: number) => void;
	onSliderChange: (value: number) => void;
}

interface HoverState {
	visible: boolean;
	time: string;
	left: number;
}

const INITIAL_HOVER_STATE: HoverState = {
	visible: false,
	time: '',
	left: 0
};

export function DraggableLayout({
	visible = true,
	volume,
	playing,
	minimized,
	playbackRate,
	progress,
	duration,
	onTogglePlay,
	onToggleSound,
	onToggleMinimized,
	onVolumeChange,
	fullscreen = false,
	onToggleFullscreen,
	onPlaybackRateChange,
	showVolume = true,
	showFullscreen = true,
	onSliderChange
}: Readonly<DraggableLayoutProps>): JSX.Element {
	const sliderRef = useRef<HTMLDivElement>(null);
	const [hoverState, setHoverState] = useState<HoverState>(INITIAL_HOVER_STATE);

	const handleMouseMove = useCallback(
		(event: MouseEvent<HTMLDivElement>): void => {
			if (!sliderRef.current || duration <= 0) return;

			const rect = sliderRef.current.getBoundingClientRect();
			const offsetX = event.clientX - rect.left;
			const percentage = Math.max(0, Math.min(1, offsetX / rect.width));
			const seconds = percentage * duration;

			setHoverState({
				visible: true,
				time: formatVideoTime(seconds),
				left: offsetX
			});
		},
		[duration]
	);

	const handleMouseLeave = useCallback((): void => {
		setHoverState(INITIAL_HOVER_STATE);
	}, []);

	const handleOnClick = useCallback(
		(event: MouseEvent<HTMLDivElement>): void => {
			if (!sliderRef.current || duration <= 0) return;

			const rect = sliderRef.current.getBoundingClientRect();
			const offsetX = event.clientX - rect.left;
			const percentage = Math.max(0, Math.min(1, offsetX / rect.width));
			const newProgress = percentage * 100;
			onSliderChange(newProgress);
		},
		[duration, onSliderChange]
	);

	return (
		<S.DraggableControlsBarWrapper
			$visible={visible}
			onClick={(event) => event.stopPropagation()}
			onMouseDown={(event) => event.stopPropagation()}
		>
			<S.MinimizeCol $visible={visible}>
				<Minimize minimized={minimized} onToggleMinimized={onToggleMinimized} />
			</S.MinimizeCol>
			<S.PlayCol $visible={visible}>
				<Player width={21} height={26} playing={playing} onTogglePlay={onTogglePlay} />
			</S.PlayCol>
			<S.ControlsBar>
				<S.SliderContainer
					ref={sliderRef}
					onMouseMove={handleMouseMove}
					onMouseLeave={handleMouseLeave}
					onClick={handleOnClick}
				>
					<S.TimeSliderWrapper value={progress} onChange={onSliderChange} tooltip={{ open: false }} />
					{hoverState.visible ? (
						<S.SliderHoverTooltip $left={hoverState.left}>{hoverState.time}</S.SliderHoverTooltip>
					) : null}
				</S.SliderContainer>
				{showVolume ? <Volume volume={volume} onToggleSound={onToggleSound} onVolumeChange={onVolumeChange} /> : null}
				<Velocity playbackRate={playbackRate} onPlaybackRateChange={onPlaybackRateChange} isDraggable />
				{showFullscreen ? <FullScreen fullscreen={fullscreen} onToggleFullscreen={onToggleFullscreen} /> : null}
			</S.ControlsBar>
		</S.DraggableControlsBarWrapper>
	);
}
